// ══════════════════════════════════════════════════════════════════
//  낮이 밤으로 들어오는 문 — **밤 쪽의 nightio.js**
//
//  ■ 지금까지 2박자가 **한 방향으로만** 흘렀다. 밤 → 낮은 전부 물려 있었지만
//    (성적 → 우유 → 손실 → 흉터 → 무리 → 다음 밤 난이도), 낮 → 밤은
//    `nightio.js` 가 키만 만들고 **전부 null** 이었다. 그래서 낮 3축을 6단계까지
//    다 사도 밤에서는 **아무 일도 일어나지 않았다** — 개축을 완주해도 개는 2마리,
//    캡스톤을 사도 무료 요령이 안 나왔다. 이 파일이 그 문이다.
//
//  ■ **훅은 여기 박고, 결합 규칙은 표로 뺀다**(명세 949행 —
//    *"트리가 바꾸는 것은 ①값 ②중첩 결합 규칙 ③갈래 소속이고,
//      바꾸지 않는 것은 그 카드가 월드의 무엇을 건드리는가다"*).
//    밤 강화 트리가 리모델링돼도 world.js 는 안 바뀐다. 표만 갈아 끼운다.
//
//  ■ ⚠ **낮 계수와 밤 카드를 어떻게 합치는지는 정본 미기재다.**
//    명세 580행이 정한 것은 **밤 카드끼리**의 규칙(`base × (1 + Σ)`, 감속만 곱연산)
//    이고, **낮 축 × 밤 카드**는 어느 문서에도 없다. 지어내지 않고,
//    아래 `MERGE` 한 곳에 **곱연산으로 잠정** 두고 그 사실을 표기한다 —
//    바꿀 때 고칠 곳이 한 줄이라야 「잠정」이 정직한 말이 된다.
//      곱연산을 고른 이유: 낮 축은 이미 자기들끼리 곱연산으로 쌓이고
//      (`day/effects.js` 의 `m[k] *= (1 + v)`), 출처가 다른 두 배율을 더하면
//      낮 축 1단계(+10%)가 밤 5중첩 위에서 체감이 사라진다.
//    **확정되면 이 상수만 바꾼다.**
// ══════════════════════════════════════════════════════════════════

// 낮 계수와 밤 카드의 합성 — 'mul' = 곱연산 / 'add' = 가산.
// **정본 미기재. 잠정값이다.**
export const MERGE = 'mul';

// ── 중립 — 낮이 아무것도 안 넘겼을 때 ────────────────────────────
// `day/effects.js` 의 `neutralDayMods()` 와 **키가 하나도 어긋나면 안 된다.**
// 어긋나면 `normalizeDayIn` 이 경고를 남긴다(조용히 삼키지 않는다).
export function neutralDayIn() {
  return {
    dmgMul: 1, rateMul: 1, fireRateMul: 1, rangeMul: 1, radiusMul: 1,
    stabRecMul: 1, stabDropMul: 1,
    dogDps: 1, dogSpeed: 1, dogDetect: 1,
    pieceMul: 1, downGuard: 0, freeCard: 0,
    stabStart: 100, priority: 'nearest',
    // 【2026-08-06 상인 확정판】 day/effects.js 와 키 계약 — 넷 신설.
    maxHpMul: 1, speedMul: 1, dmgTakenMul: 1, waterHealMul: 1,
  };
}

// ── 어디로 가는가 ────────────────────────────────────────────────
// `mods` = 밤 `mods.js` 에 **같은 이름이 이미 있는 것**. 그대로 합쳐진다.
// `world` = 밤에 같은 이름이 없어 `world.js` 가 직접 읽는 것.
export const DAY_IN = {
  dmgMul:      { into: 'mods',  key: 'dmgMul' },
  rangeMul:    { into: 'mods',  key: 'rangeMul' },
  radiusMul:   { into: 'mods',  key: 'radiusMul' },
  dogDps:      { into: 'mods',  key: 'dogDps' },
  dogSpeed:    { into: 'mods',  key: 'dogSpeed' },
  dogDetect:   { into: 'mods',  key: 'dogDetect' },

  rateMul:     { into: 'world', at: '(구 지팡이 스윙 간격 — 지팡이 삭제로 수신자 없음. 키는 계약이라 남긴다)' },
  fireRateMul: { into: 'world', at: 'swingInterval() — 물맷돌 발사 간격' },
  stabRecMul:  { into: 'world', at: 'updateStability() — 회복' },
  stabDropMul: { into: 'world', at: 'updateStability() — 감소' },
  pieceMul:    { into: 'world', at: 'dropPiece() — 조각 획득' },
  downGuard:   { into: 'world', at: 'step() — 밤 1회 다운 무효' },
  freeCard:    { into: 'world', at: 'openShop() — 밤마다 무료 요령' },
  stabStart:   { into: 'world', at: 'constructor — 안정성 시작값' },
  priority:    { into: 'world', at: 'autoWeapon() — 타겟 우선순위' },
  // 【2026-08-06】 상인 확정판의 넷 — 둘은 밤 mods 기존 훅으로, 둘은 world 직행
  speedMul:    { into: 'mods',  key: 'moveMul' },      // 「신발 끈」 훅이 이미 있다
  dmgTakenMul: { into: 'mods',  key: 'dmgTakenMul' },  // 물량·정예 피격에 이미 걸려 있다
  maxHpMul:    { into: 'world', at: 'constructor — 최대 HP' },
  waterHealMul:{ into: 'world', at: 'drinkWater() — 한 모금' },
};

// 타겟 우선순위 — 낮 3축이 여는 것들(`day/ledger.js` 가 settings.priority 를 쓴다).
//   nearest = 기본 / elite = 정예 우선(물맷돌축 2단계 구 elitePriority)
//   far     = 먼 적 우선(물맷돌축 4단계) / dense = 밀집 우선(캡스톤)
export const PRIORITIES = ['nearest', 'elite', 'far', 'dense'];

// 「밀집 우선」의 점수식 — ⚠ **정본 미기재. 잠정.**
// 정본이 정한 것은 이름뿐이고 「무엇을 밀집으로 세는가」는 어느 문서에도 없다.
//   r = 붐빔을 세는 칸 크기 · w = 이웃 한 마리당 당겨오는 거리(픽셀 환산 감점)
// w 가 크면 멀어도 뭉친 쪽을 친다. 확정되면 이 둘만 바꾼다.
export const DENSE = { r: 4 * 32, w: 40 };

// ── 받는다 ───────────────────────────────────────────────────────
// **모르는 키를 조용히 삼키지 않는다.** 낮이 새 훅을 만들었는데 밤이 자리를
// 안 팠으면 그 사실이 보여야 한다 — 이 프로젝트가 실제로 그 사고를 냈다
// (낮 3축 전부가 밤에 안 닿는 채로 30밤이 돌았다).
export function normalizeDayIn(day, warn = null) {
  const out = neutralDayIn();
  if (!day) return out;
  const unknown = [];
  for (const [k, v] of Object.entries(day)) {
    if (!(k in out)) { unknown.push(k); continue; }
    if (k === 'priority') { if (PRIORITIES.includes(v)) out[k] = v; else unknown.push(`priority=${v}`); }
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    else unknown.push(`${k}=${v}`);
  }
  if (unknown.length) {
    const msg = `dayin: 밤이 못 받는 낮 훅 ${unknown.length}개 — ${unknown.join(', ')}`;
    if (warn) warn(msg);
    else if (typeof console !== 'undefined') console.warn(msg);
  }
  return out;
}

// ── 밤 mods 에 합친다 ────────────────────────────────────────────
// **호출부는 무엇이 합쳐졌는지 몰라도 된다** — 반환 모양이 같다.
// mods.js 가 세워 둔 벽 그대로다.
export function mergeDayIn(nightMods, day) {
  for (const [dk, spec] of Object.entries(DAY_IN)) {
    if (spec.into !== 'mods') continue;
    const nk = spec.key;
    if (!(nk in nightMods)) continue;          // 밤에 그 훅이 없으면 건너뛴다
    const dv = day[dk];
    if (dv === 1) continue;                    // 중립 — 곱해도 더해도 그대로
    nightMods[nk] = MERGE === 'mul' ? nightMods[nk] * dv
                                    : nightMods[nk] + (dv - 1);
  }
  return nightMods;
}
